import React from 'react';
import { Link } from 'react-router-dom';
import { User } from 'lucide-react';
import { formatHandle, getProfilePath } from '../utils/formatHandle';

interface PostAuthorLinkProps {
  authorId: string;
  authorName: string;
  authorHandle?: string;
  authorImageUrl?: string;
  timestamp?: string;
  className?: string;
  onNavigate?: () => void;
}

const PostAuthorLink: React.FC<PostAuthorLinkProps> = ({
  authorId,
  authorName,
  authorHandle,
  authorImageUrl,
  timestamp,
  className = '',
  onNavigate,
}) => {
  if (!authorId) {
    return (
      <div className={`flex items-center ${className}`}>
        {authorImageUrl ? (
          <img src={authorImageUrl} alt={authorName} className="w-10 h-10 rounded-full mr-3 object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full mr-3 bg-dark-bg flex items-center justify-center">
            <User className="w-5 h-5 text-text-muted" />
          </div>
        )}
        <div>
          <h3 className="text-text-primary font-semibold">
            {authorName}
            {authorHandle && (
              <span className="text-text-muted text-sm ml-2">{formatHandle(authorHandle)}</span>
            )}
          </h3>
          {timestamp && <p className="text-text-muted text-sm">{timestamp}</p>}
        </div>
      </div>
    );
  }

  return (
    <Link
      to={getProfilePath(authorId, authorHandle)}
      onClick={(e) => {
        e.stopPropagation();
        onNavigate?.();
      }}
      className={`flex items-center group rounded-lg -m-1 p-1 hover:bg-dark-card/50 transition-colors ${className}`}
      title={`View ${authorName}'s profile`}
    >
      {authorImageUrl ? (
        <img
          src={authorImageUrl}
          alt={authorName}
          className="w-10 h-10 rounded-full mr-3 object-cover ring-2 ring-transparent group-hover:ring-accent-blue/40 transition-all"
        />
      ) : (
        <div className="w-10 h-10 rounded-full mr-3 bg-dark-bg flex items-center justify-center ring-2 ring-transparent group-hover:ring-accent-blue/40 transition-all">
          <User className="w-5 h-5 text-text-muted" />
        </div>
      )}
      <div>
        <h3 className="text-text-primary font-semibold group-hover:text-accent-blue transition-colors">
          {authorName}
          {authorHandle && (
            <span className="text-text-muted text-sm ml-2 group-hover:text-accent-blue/80">
              {formatHandle(authorHandle)}
            </span>
          )}
        </h3>
        {timestamp && <p className="text-text-muted text-sm">{timestamp}</p>}
      </div>
    </Link>
  );
};

export default PostAuthorLink;
